import { Box } from "@mui/material";
import { ActionsCell } from "../../../../../Component/Common/Components/DataTable/ActionsCell";
import { ActionsCellWithBadge } from "../../../../../Component/Common/Components/DataTable/ActionsCellWithBadge";
import { DefaultCell } from "../../../../../Component/Common/Components/DataTable/DefaultCell";
import { IRowAction } from "../../../../../Component/Common/Components/DataTable/RowAction";
import { Deviations } from "../../../../../Component/Model/Dto/EmploymentEquity/Deviations";
import { DataTableHtmlFormatter } from "../../../../../Component/Model/helpers";
import { TableColumn } from "../../../../../Component/Model/Table/Table";
import colors from "../../../../../assets/theme/base/colors";

interface OccupationalLevelJustificationsRow {
    id: number;
    justifiableReason: string;
    comment: string;
    eea2KeyJustifiableReason: string;
    SupportingDocuments: IRowAction[];
    Actions: IRowAction[];
}

interface OccupationalLevelJustificationsTableData {
    columns: Array<TableColumn>;
    rows: Array<OccupationalLevelJustificationsRow>;
}

export function OccupationalLevelJustificationsTableMapper(
    dataObject: Deviations.SearchResultDto[],
    handleEdit: (id: number) => void,
    handleDelete: (id: number) => void,
    handleDocuments: (id: number) => void
): OccupationalLevelJustificationsTableData {
    const columns: TableColumn[] = [
        {
            Header: "Justification Reason",
            accessor: "justifiableReason",
            Cell: ({ value }: any) => <DefaultCell value={value} tooltip={true} maxWidth={"350px"} />,
            disableSortBy: true,
        },
        {
            Header: "Commentary",
            accessor: "comment",
            Cell: ({ value }: any) => <DefaultCell value={value} tooltip={true} maxWidth={"350px"} />,
            disableSortBy: true,
        },
        {
            Header: "EEA2 Key Justifiable Reason",
            accessor: "eea2KeyJustifiableReason",
            Cell: ({ value }: any) => {
                if (!value) { return <DefaultCell value="" />; }
                const isYes = value === "Yes";
                return (
                    <Box
                        component="span"
                        sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            px: 1.25,
                            py: 0.25,
                            borderRadius: "9999px",
                            fontSize: "0.75rem",
                            fontWeight: 500,
                            lineHeight: 1.5,
                            backgroundColor: isYes ? colors.eea2KeyReason.yes.background : colors.eea2KeyReason.no.background,
                            color: isYes ? colors.eea2KeyReason.yes.text : colors.eea2KeyReason.no.text,
                        }}
                    >
                        {value}
                    </Box>
                );
            },
            disableSortBy: true,
        },
        {
            Header: "Documents",
            accessor: "SupportingDocuments",
            Cell: ({ value }: any) => <ActionsCellWithBadge actions={value} />,
            disableSortBy: true,
        },
        {
            Header: "Actions",
            accessor: "Actions",
            Cell: ({ value }: any) => <ActionsCell actions={value} />,
            disableSortBy: true,
        },
    ];

    const rows = (dataObject ?? []).map((item: Deviations.SearchResultDto) => ({
        id: item.id,
        justifiableReason: item.justifiableReason ?? "",
        comment: DataTableHtmlFormatter(item.comment ?? "", " "),
        eea2KeyJustifiableReason: item.eea2KeyJustifiableReason === true ? "Yes" : item.eea2KeyJustifiableReason === false ? "No" : "",
        SupportingDocuments: [
            {
                label: "Supporting Documents",
                key: String(item.id),
                action: (id: string) => handleDocuments(Number(id)),
                badgeCount: item.documentCount,
                showBadge: true,
                icon: { color: "info" as const, name: "attach_file" },
            },
        ],
        Actions: [
            {
                label: "Edit",
                key: String(item.id),
                action: (id: string) => handleEdit(Number(id)),
                icon: { color: "info" as const, name: "edit" },
            },
            {
                label: "Delete",
                key: String(item.id),
                action: (id: string) => handleDelete(Number(id)),
                icon: { color: "error" as const, name: "delete" },
            },
        ],
    }));

    return { columns, rows };
}
